import { html, nothing, type PropertyValues } from 'lit';
import { property, query } from 'lit/decorators.js';
import { LuxenElement } from '../../shared/luxen-element.js';
import { HasSlotController } from '../../shared/controllers/has-slot-controller.js';
import { ShowEvent, AfterShowEvent, HideEvent, AfterHideEvent } from '../../events/index.js';
import hostStyles from '../../shared/styles/host.styles.js';
import styles from './dialog.styles.js';

interface CommandEventLike extends Event {
  command: string;
  source: Element | null;
}

const supportsClosedBy =
  typeof HTMLDialogElement !== 'undefined' && 'closedBy' in HTMLDialogElement.prototype;

// Native <dialog> doesn't lock body scroll. Inject a global rule that uses
// `:has()` to freeze the root scroll container whenever any modal l-dialog
// is open. Purely declarative — no manual lock/unlock bookkeeping.
// Symbol guard makes the injection idempotent across HMR reloads.
//
// `scrollbar-gutter: stable` only applies to centered modals — it prevents
// the page behind from shifting horizontally when the scrollbar disappears.
// Edge-attached modals (drawers) opt out via `data-modal="edge"`: a reserved
// gutter would push the drawer off the actual viewport edge.
const SCROLL_LOCK_SHEET = Symbol.for('luxen-dialog-scroll-lock');
if (typeof document !== 'undefined' && !(SCROLL_LOCK_SHEET in document)) {
  const sheet = new CSSStyleSheet();
  sheet.replaceSync(`
    html:has([data-modal]) { overflow: hidden; }
    html:has([data-modal="centered"]) { scrollbar-gutter: stable; }
  `);
  document.adoptedStyleSheets.push(sheet);
  Object.defineProperty(document, SCROLL_LOCK_SHEET, { value: sheet });
}

/**
 * A modal dialog rendered in the top layer via the native `<dialog>` element.
 *
 * Open and close by toggling the `open` property (or the `--show` / `--hide`
 * Invoker commands). There are no public `show()` / `close()` methods.
 *
 * @slot - Body content.
 * @slot title - Custom heading element. Overrides the default `<h2>` rendered from the `title` property. Also provides the dialog's accessible name.
 * @slot close - Close button (typically `<button class="l-close">`).
 * @slot footer - Footer actions.
 *
 * @csspart dialog - The native `<dialog>` element.
 * @csspart header - The header wrapper containing the title and close slot.
 * @csspart title - The dialog title heading.
 * @csspart body - The body wrapper around the default slot.
 * @csspart footer - The footer wrapper around the footer slot.
 *
 * @cssproperty [--width=31rem] - Dialog width.
 * @cssproperty [--border-radius=6px] - Dialog border radius.
 * @cssproperty [--padding=1.5rem] - Padding applied to the header, footer, and inline-padding of the body. Set to `0` to remove all internal spacing (e.g. for edge-to-edge media).
 * @cssproperty [--show-duration=200ms] - Open transition duration.
 * @cssproperty [--hide-duration=200ms] - Close transition duration.
 * @cssproperty --backdrop - Backdrop color.
 * @cssproperty [--backdrop-blur=0] - Backdrop blur amount (any CSS length). `0` means no blur; set e.g. `4px` for a subtle frost.
 *
 * @event show - Fired when the dialog is about to open. Cancelable — call `event.preventDefault()` to keep it closed.
 * @event after-show - Fired after the open animation completes. Not cancelable.
 * @event hide - Fired when the dialog is about to close. Cancelable — call `event.preventDefault()` to keep it open.
 * @event after-hide - Fired after the close animation completes. Not cancelable.
 *
 * @command --show - Sets `open = true`.
 * @command --hide - Sets `open = false`.
 *
 * @customElement l-dialog
 */
export class Dialog extends LuxenElement {
  static styles = [hostStyles, styles];

  /** Dialog title rendered in the header and used as the dialog's accessible name. */
  @property()
  title = '';

  /** Whether the dialog is open. */
  @property({ type: Boolean, reflect: true })
  open = false;

  /** Close when the backdrop is clicked. */
  @property({ type: Boolean, reflect: true, attribute: 'light-dismiss' })
  lightDismiss = false;

  /** Hide the header entirely (title and close slot). */
  @property({ type: Boolean, reflect: true, attribute: 'without-header' })
  withoutHeader = false;

  // Tracks whether the footer slot has content, so the footer row can collapse
  // when empty (toggles `data-empty` below). A controller drives this because
  // `:host(:has(> [slot='footer']))` is invalid — `:has()` can't be nested in
  // `:host()`, so browsers drop the whole rule.
  private _slots = new HasSlotController(this, 'footer');

  @query('dialog')
  dialog!: HTMLDialogElement;

  // Drives the scroll-lock stylesheet: `centered` reserves the scrollbar
  // gutter (no page shift); `edge` skips it (drawers sit flush to the edge).
  protected _modalKind: 'centered' | 'edge' = 'centered';

  // Subclass seams (defaults keep `l-dialog` unchanged). `alert-dialog`
  // overrides these to expose `role="alertdialog"` and wire `aria-describedby`
  // to the body. `_bodyId` drives both the body's `id` and `aria-describedby`,
  // so the association can't drift; an empty string emits neither.
  protected get _role(): string {
    return '';
  }
  protected get _bodyId(): string {
    return '';
  }

  private _mouseDownTarget: EventTarget | null = null;

  private _commandListener: EventListenerObject = {
    handleEvent: (e: Event) => this._onCommand(e as CommandEventLike),
  };

  // --- Lifecycle ---

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('command', this._commandListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('command', this._commandListener);
  }

  firstUpdated() {
    this.dialog.addEventListener('cancel', (e) => this._onCancel(e));
    this.dialog.addEventListener('close', () => this._onNativeClose());
    this.dialog.addEventListener('mousedown', (e) => {
      this._mouseDownTarget = e.target;
    });
    this.dialog.addEventListener('click', (e) => this._onDialogClick(e));
  }

  updated(changed: PropertyValues<this>) {
    if (!changed.has('open')) return;

    if (this.open && !this.dialog.open) {
      // Opening — cancelable. Revert the property if consumer prevents.
      if (!this.dispatchEvent(new ShowEvent())) {
        // Defer the revert so it lands outside the current update cycle and
        // doesn't schedule a new update from inside updated().
        queueMicrotask(() => {
          this.open = false;
        });
        return;
      }
      this.setAttribute('data-modal', this._modalKind);
      this.dialog.showModal();
      this._focusInitial();
      void this._emitAfter('after-show');
    } else if (!this.open && this.dialog.open) {
      // Closing — cancelable. Revert the property if consumer prevents.
      if (!this.dispatchEvent(new HideEvent())) {
        // Defer the revert so it lands outside the current update cycle and
        // doesn't schedule a new update from inside updated().
        queueMicrotask(() => {
          this.open = true;
        });
        return;
      }
      this.dialog.close();
      // `after-hide` is emitted from `_onNativeClose` (runs for every close path).
    }
  }

  // --- Event handlers ---

  // Custom commands on a custom element must start with `--`.
  // Built-in commands like "close" are reserved for native elements
  // and won't fire here.
  private _onCommand(e: CommandEventLike) {
    switch (e.command) {
      case '--hide':
        this.open = false;
        break;
      case '--show':
        this.open = true;
        break;
    }
  }

  // Fires on Escape and on `closedby="any"` close requests.
  // Does NOT fire when script calls `.close()`, so `updated()`'s cancelable
  // `hide` emit doesn't collide with this one.
  protected _onCancel(e: Event) {
    if (!this.dispatchEvent(new HideEvent())) {
      e.preventDefault();
    }
  }

  private _onNativeClose() {
    this.open = false;
    this.removeAttribute('data-modal');
    void this._emitAfter('after-hide');
  }

  private _onDialogClick(e: MouseEvent) {
    // With `dialog { padding: 0 }`, `e.target === this.dialog` only fires
    // for backdrop clicks. The mousedown guard prevents drag-out dismissal.
    const clickedBackdrop = e.target === this.dialog && this._mouseDownTarget === this.dialog;
    this._mouseDownTarget = null;
    if (!clickedBackdrop) return;

    if (this.lightDismiss) {
      // When `supportsClosedBy`, the native `closedby="any"` already closed.
      if (!supportsClosedBy) this.open = false;
      return;
    }
    this._nudgeDismiss();
  }

  private _nudgeAnimation?: Animation;

  private _nudgeDismiss() {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this._nudgeAnimation?.cancel();
    this._nudgeAnimation = this.dialog.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(1.02)' }, { transform: 'scale(1)' }],
      { duration: 250, easing: 'ease-in-out' },
    );
  }

  // Awaits every active animation on the dialog (transitions + @keyframes)
  // and then emits. Resolves immediately when no animations are running,
  // which covers `prefers-reduced-motion` and consumers that zero the
  // duration custom properties — cases where `transitionend` never fires.
  // Waits one frame first so @starting-style transitions have registered.
  private async _emitAfter(name: 'after-show' | 'after-hide') {
    await new Promise((r) => requestAnimationFrame(() => r(null)));
    const anims = this.dialog.getAnimations({ subtree: false });
    await Promise.all(anims.map((a) => a.finished.catch(() => {})));
    if ((name === 'after-show') !== this.open) return;
    this.dispatchEvent(name === 'after-show' ? new AfterShowEvent() : new AfterHideEvent());
  }

  // Firefox/Safari don't reliably resolve `[autofocus]` when `<dialog>`
  // is in shadow DOM and the target is in light DOM. Resolve it manually.
  // Subclasses (alert-dialog) override to fall back to a shadow-DOM target.
  protected _focusInitial() {
    const target = this.querySelector<HTMLElement>('[autofocus]');
    target?.focus({ preventScroll: true });
  }

  render() {
    const closedby = this.lightDismiss && supportsClosedBy ? 'any' : nothing;
    return html`
      <dialog
        part="dialog"
        role=${this._role || nothing}
        closedby=${closedby}
        aria-labelledby=${this.withoutHeader ? nothing : 'title-area'}
        aria-label=${this.title || nothing}
        aria-describedby=${this._bodyId || nothing}
      >
        ${this.withoutHeader
          ? nothing
          : html`
              <header part="header">
                <slot
                  name="title"
                  id="title-area"
                >
                  ${this.title ? html`<h2 part="title">${this.title}</h2>` : nothing}
                </slot>
                <slot name="close"></slot>
              </header>
            `}
        <div
          part="body"
          id=${this._bodyId || nothing}
        >
          <slot></slot>
        </div>
        ${this.renderFooter()}
      </dialog>
    `;
  }

  // Footer row. Overridden by subclasses (alert-dialog) to render their own
  // actions in place of the default `footer` slot.
  protected renderFooter() {
    return html`
      <footer
        part="footer"
        ?data-empty=${!this._slots.test('footer')}
      >
        <slot name="footer"></slot>
      </footer>
    `;
  }
}
